import { useState, useCallback, useMemo, useEffect } from 'react';
import type { WizardShellProps, WizardStepId } from '../../../types/wizardTypes';
import { WIZARD_STEPS } from '../../../enums/wizardSteps';
import WizardStepper from './WizardStepper';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import { getToolValidationError } from '../../../lib/toolCompletion';
import { useConnectionsStore } from '../../../store/connectionsStore';
import type { McpTool } from '../../../types/types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './WizardShell.module.css';

export default function WizardShell({
  children,
  watch,
  isSuperAdmin = false,
  goToStepRef,
  onStepChange,
  errorStepIndexes = new Set<number>(),
}: WizardShellProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const name = watch('name');
  const title = watch('title');
  const description = watch('description');
  const mainConnectionId = watch('mainConnectionId');
  const path = watch('path');
  const httpMethod = watch('httpMethod');
  const bodyTemplate = watch('bodyTemplate');
  const inputSchema = watch('inputSchema');
  const queryMap = watch('queryMap');
  const annotations = watch('annotations');
  const responseTemplate = watch('responseTemplate');

  const connectors = useConnectionsStore(s => s.connectors);
  const isMssqlConnector = connectors.find(c => c.id === mainConnectionId)?.connectionTypeId === 7;

  const visibleSteps = useMemo(() => {
    if (isSuperAdmin) return WIZARD_STEPS;
    return WIZARD_STEPS.filter(s => s.id !== 'response-mapping');
  }, [isSuperAdmin]);

  const completedSteps = useMemo(() => {
    const completed = new Set<WizardStepId>();

    // Tool Info: requires name, title, description, and a connector
    if (name && title && description && mainConnectionId) {
      completed.add('tool-info');
    }

    // Request Mapping: requires path; body template only needed for non-GET methods
    const isGet = httpMethod === 'GET';
    const isSelect = httpMethod === 'SELECT'|| isMssqlConnector;
    if (isSelect ? bodyTemplate : (path && (isGet || bodyTemplate))) {
      completed.add('request-mapping');
    }

    // Input Fields: has a non-empty input schema and no template variable mismatches
    if (inputSchema && inputSchema !== '{}' && inputSchema !== '') {
      const parsedSchema = typeof inputSchema === 'string' ? (() => { try { return JSON.parse(inputSchema); } catch { return undefined; } })() : inputSchema;
      const toolLike = { name, description, queryMap, path, httpMethod, bodyTemplate, inputSchema: parsedSchema } as McpTool;
      const validationError = getToolValidationError(toolLike, isMssqlConnector);
      if (!validationError) {
        completed.add('input-fields');
      }
    }

    // Response Mapping: has a non-default response template
    if (responseTemplate && responseTemplate.trim() !== '') {
      completed.add('response-mapping');
    }

    // Chat Quick Actions: has annotations configured
    if (annotations && annotations !== '{}' && annotations !== '') {
      completed.add('chat-quick-actions');
    }

    return completed;
  }, [name, title, description, mainConnectionId, path, httpMethod, bodyTemplate, inputSchema, queryMap, responseTemplate, annotations, isMssqlConnector]);

  const totalSteps = visibleSteps.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < totalSteps) {
      setCurrentStep(index);
    }
  }, [totalSteps]);

  useEffect(() => {
    if (goToStepRef) {
      goToStepRef.current = goToStep;
    }
  }, [goToStepRef, goToStep]);

  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  const currentStepConfig = visibleSteps[currentStep];

  return (
    <div className={styles.shell}>
      <WizardStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={goToStep}
        errorSteps={errorStepIndexes}
        steps={visibleSteps}
      />

      <div className={styles.toolbar}>
        <div className={styles.stepInfo}>
          <div className={styles.stepTitleRow}>
            <h2 className={styles.stepTitle}>{currentStepConfig.label}</h2>
            <InfoLabel content={currentStepConfig.subtitle} />
          </div>
        </div>
        <div className={styles.toolbarActions}>

        </div>
      </div>

      <div className={styles.mainContent}>
        <div
          className={styles.stepContent}
          role="tabpanel"
          id={`wizard-panel-${currentStepConfig.id}`}
          aria-labelledby={`wizard-tab-${currentStepConfig.id}`}
        >
          {children(currentStepConfig.id)}
        </div>

        {/* {showPreview && (
          <aside className={styles.previewPanel} aria-label="Live preview">
            <ChatPreview watch={watch} mode={mode} />
          </aside>
        )} */}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={isFirstStep ? undefined : goBack}
          disabled={isFirstStep}
        >
          <ChevronLeft size={16} />
          Back
        </button>

        <div className={styles.footerRight}>
          {!isLastStep && (
            <button
              type="button"
              className={styles.nextBtn}
              onClick={goNext}
            >
              Continue
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
